import { useQuery } from '@tanstack/react-query';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef, useState } from 'react';

import { AD_SLOTS } from 'components/AdSlots/AdSenseSlot';
import LazyHydratedAdSenseSlot from 'components/AdSlots/AdSenseSlot/LazyHydratedAdSenseSlot';
import Layout from 'components/Layout';

import useSendPageView from 'hooks/useSendPageView';

import { threadQueryKeys } from 'lib/queryKeys';
import { sendSupixEvent } from 'lib/supix';

import { useUser } from 'providers/UserProvider';
import { useUtm } from 'providers/UtmProvider';

import { getThreadInfo } from 'services/thread';

import DynamicallyLoadedCommentsList from './CommentsList/DynamicallyLoadedCommentsList';
import DynamicallyLoadedBlogArticles from './DynamicallyLoadedBlogArticles';
import ThreadHeading from './Heading';
import ThreadMainContent from './MainContent';
import ThreadMainContentHead from './MainContentHead';
import DynamicallyLoadedOffersCarousel from './OffersCarousel/DynamicallyLoadedOffersCarousel';
import ThreadSchema from './Schema';
import Sidebar from './Sidebar';

import FORUM from 'constants/forum';
import LINKS from 'constants/links';
import STATUS_CODES from 'constants/statusCodes';

const Loading = dynamic(() => import('./Loading'), { ssr: false });

const GA_EVENT_CATEGORY = 'forum_post';

const Thread = ({
  sidebarFeaturedOffers = [],
  thread: serverThread,
  threadSlug,
}) => {
  const commentsRef = useRef(null);
  const router = useRouter();
  const { loadingLoggedInUser } = useUser();
  const { isUtmLoaded } = useUtm();
  const [threadLikesCount, setThreadLikesCount] = useState(
    serverThread?.threadLikes ? serverThread.threadLikes : 0
  );

  const { data: clientThread } = useQuery({
    queryKey: threadQueryKeys.thread({ threadKey: threadSlug }),
    queryFn: ({ signal }) => getThreadInfo({ threadSlug }, { signal }),
    enabled: !serverThread && !loadingLoggedInUser,
    onSuccess: (threadFromClient) => {
      setThreadLikesCount(threadFromClient.threadLikes);
    },
    onError: ({ message }) => {
      if (message === STATUS_CODES[401]) {
        router.push(LINKS.HOME);
      }
    },
  });

  const onAddLike = useCallback((action) => {
    setThreadLikesCount((prev) =>
      action === FORUM.THREAD.ACTIONS.LIKE ? prev + 1 : prev - 1
    );
  }, []);

  const thread = serverThread || clientThread;

  useEffect(() => {
    if (isUtmLoaded && thread?.threadId) {
      sendSupixEvent('/fr', {
        id: thread.threadId,
        cid: thread.threadCategory,
      });
    }
  }, [thread?.threadId, thread?.threadCategory, isUtmLoaded]);

  useSendPageView(thread?.threadId, 'thread', {
    enabled: !!thread?.threadId,
  });

  if (!thread) {
    return <Loading />;
  }

  return (
    <>
      <ThreadSchema {...thread} threadLikesCount={threadLikesCount} />
      <Layout
        gridProps={{ className: 'mb-14' }}
        headingComponent={
          <ThreadHeading
            threadCategoryName={thread.threadCategoryName}
            threadCategorySlug={thread.threadCategorySlug}
            threadTitle={thread.threadTitle}
          />
        }
        sidebarComponent={<Sidebar featuredOffers={sidebarFeaturedOffers} />}
        maxWidthOnMobileDevices
      >
        <ThreadMainContentHead
          commentsRef={commentsRef}
          gaEventCategory={GA_EVENT_CATEGORY}
          onAddLike={onAddLike}
          {...thread}
          threadLikes={threadLikesCount}
        />
        <LazyHydratedAdSenseSlot
          className="mt-4"
          height={50}
          showAdSlotOnBreakpoints={[true, null, false]}
          slotId={AD_SLOTS.LANDSCAPE_320x50}
        />
        <ThreadMainContent
          gaEventCategory={GA_EVENT_CATEGORY}
          isCommentsAllowed={thread.isCommentsAllowed}
          threadCommentsCount={thread.threadCommentsCount}
          threadId={thread.threadId}
          threadTags={thread.threadTags}
          threadText={thread.threadText}
          threadTitle={thread.threadTitle}
        />
        <LazyHydratedAdSenseSlot
          className="my-4"
          height={50}
          showAdSlotOnBreakpoints={[true, null, false]}
          slotId={AD_SLOTS.LANDSCAPE_320x50}
        />
        <DynamicallyLoadedCommentsList
          ref={commentsRef}
          className="mt-6"
          isCommentsAllowed={!thread.threadSolved}
          threadCommentsCount={thread.threadComments}
          threadId={thread.threadId}
        />
        <LazyHydratedAdSenseSlot
          className="mt-4"
          height={50}
          showAdSlotOnBreakpoints={[true, null, false]}
          slotId={AD_SLOTS.LANDSCAPE_320x50}
        />
        {thread.relatedOffers.length > 0 && (
          <DynamicallyLoadedOffersCarousel
            gaEventCategory={GA_EVENT_CATEGORY}
            relatedOffers={thread.relatedOffers}
            threadId={thread.threadId}
          />
        )}
        {thread.relatedArticles.length > 0 && (
          <DynamicallyLoadedBlogArticles
            gaEventCategory={GA_EVENT_CATEGORY}
            relatedArticles={thread.relatedArticles}
          />
        )}
      </Layout>
    </>
  );
};

Thread.propTypes = {
  sidebarFeaturedOffers: PropTypes.arrayOf(
    PropTypes.shape({
      offerId: PropTypes.number.isRequired,
      offerTitle: PropTypes.string.isRequired,
      offerPrice: PropTypes.number.isRequired,
      offerOldPrice: PropTypes.number,
      offerPriceType: PropTypes.string.isRequired,
      offerPhoto: PropTypes.string.isRequired,
      offerSlug: PropTypes.string.isRequired,
    })
  ),
  thread: PropTypes.shape().isRequired,
  threadSlug: PropTypes.string.isRequired,
};

export default Thread;
